import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";

export type SavedViewEntityType = "people" | "deals" | "companies" | "tasks" | "activities";

export interface SavedViewFilter {
  field: string;
  operator: string;
  value: unknown;
}

export interface SavedViewSort {
  field: string;
  direction: "asc" | "desc";
}

export interface SavedViewRecord {
  id: string;
  name: string;
  entityType: SavedViewEntityType;
  filters: SavedViewFilter[];
  sort: SavedViewSort[];
  columns: string[];
  grouping: Record<string, unknown> | null;
  isDefault: boolean;
  isShared: boolean;
  ownerId: string;
  pinOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

interface SavedViewsResponse {
  data: SavedViewRecord[];
}

interface SavedViewResponse {
  data: SavedViewRecord;
}

interface CreateSavedViewInput {
  name: string;
  entityType: SavedViewEntityType;
  filters?: SavedViewFilter[];
  sort?: SavedViewSort[];
  columns?: string[];
  grouping?: Record<string, unknown>;
  isDefault?: boolean;
  isShared?: boolean;
  pinOrder?: number;
}

interface UpdateSavedViewInput {
  name?: string;
  filters?: SavedViewFilter[];
  sort?: SavedViewSort[];
  columns?: string[];
  grouping?: Record<string, unknown>;
  isDefault?: boolean;
  isShared?: boolean;
  pinOrder?: number;
}

export function useSavedViews(entityType: SavedViewEntityType) {
  const [views, setViews] = useState<SavedViewRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchViews = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const searchParams = new URLSearchParams({ entityType });
      const response = await apiFetch<SavedViewsResponse>(`/crm/views?${searchParams.toString()}`);
      setViews(response.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load saved views");
    } finally {
      setIsLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    void fetchViews();
  }, [fetchViews]);

  return { views, isLoading, error, refetch: fetchViews };
}

export async function createSavedView(input: CreateSavedViewInput): Promise<SavedViewRecord> {
  const response = await apiFetch<SavedViewResponse>("/crm/views", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function updateSavedView(id: string, input: UpdateSavedViewInput): Promise<SavedViewRecord> {
  const response = await apiFetch<SavedViewResponse>(`/crm/views/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function deleteSavedView(id: string): Promise<void> {
  await apiFetch(`/crm/views/${id}`, { method: "DELETE" });
}
